/**
 * 録音機能 composable（Recording ページと Practice ページで共通利用）
 * ReliableRecorder の起動・停止・進捗表示・提出完了コールバックを提供
 */

import { ref, nextTick } from 'vue'
import { ReliableRecorder } from '../utils/reliableRecorder.js'

export function useRecording(options = {}) {
  const {
    currentUserRef,
    /** 兄弟対応: 選択中の子（learners.id）。録音完了時に API に渡す */
    selectedLearnerIdRef = null,
    guardHoliday = () => true,
    onSubmissionSuccess = () => {},
    /** 録音停止ボタン押下直後に呼ぶ（type, durationSeconds）。送信済み（処理中）表示用 */
    onRecordingStopRequested = () => {},
    /** 録音が実際に開始された直後に呼ぶ（iOS で動画音量維持のため「録音→再生」順にするときに使用） */
    onRecordingStarted = () => {}
  } = options

  // 録音状態
  const isRecording = ref(false)
  const currentRecordingDay = ref(null)
  const currentRecordingType = ref(null)
  const canStopRecording = ref(false)
  const recordingTime = ref(0)
  const displayTime = ref(0)
  const displayTimeInterval = ref(null)
  const reliableRecorder = ref(null)
  const currentRecordingId = ref(null)

  // 録音停止ボタン押下（手動停止）か、最大時間等による自動停止かを区別する
  let stopRequestedByUser = false

  // 本番用アップロード進捗表示
  const showProductionUploadProgress = ref(false)
  const productionUploadProgress = ref({
    total: 0,
    completed: 0,
    percentage: 0
  })
  const uploadStatus = ref('uploading')

  let uploadMonitorInterval = null

  const startDisplayTimeCounter = () => {
    displayTime.value = 0
    displayTimeInterval.value = setInterval(() => {
      displayTime.value++
    }, 1000)
    console.log('⏰ 表示時間カウンター開始')
  }

  const stopDisplayTimeCounter = () => {
    if (displayTimeInterval.value) {
      clearInterval(displayTimeInterval.value)
      displayTimeInterval.value = null
    }
    console.log('⏹️ 表示時間カウンター停止')
  }

  const handleUploadComplete = async () => {
    try {
      productionUploadProgress.value.percentage = 100
      productionUploadProgress.value.completed = productionUploadProgress.value.total
      await new Promise(resolve => setTimeout(resolve, 1000))
      uploadStatus.value = 'completed'
      await new Promise(resolve => setTimeout(resolve, 2000))
      showProductionUploadProgress.value = false
      uploadStatus.value = 'uploading'
      console.log('✅ アップロード進捗表示完了')
    } catch (error) {
      console.error('❌ アップロード完了処理エラー:', error)
    }
  }

  const startUploadMonitoring = () => {
    const checkInterval = 500
    const maxWaitTime = 30000
    let waitTime = 0

    uploadMonitorInterval = setInterval(async () => {
      try {
        const { IndexedDBStorage } = await import('../utils/indexedDBStorage.js')
        const storage = new IndexedDBStorage()
        const unuploadedChunks = await storage.getUnuploadedChunks()
        const currentRecordingChunks = unuploadedChunks.filter(
          chunk => chunk.recordingId === currentRecordingId.value
        )

        const completedChunks = productionUploadProgress.value.total - currentRecordingChunks.length
        productionUploadProgress.value.completed = completedChunks
        productionUploadProgress.value.percentage = productionUploadProgress.value.total > 0
          ? Math.round((completedChunks / productionUploadProgress.value.total) * 100)
          : 0

        if (completedChunks >= productionUploadProgress.value.total) {
          clearInterval(uploadMonitorInterval)
          uploadMonitorInterval = null
          await handleUploadComplete()
        }

        waitTime += checkInterval
        if (waitTime >= maxWaitTime) {
          clearInterval(uploadMonitorInterval)
          uploadMonitorInterval = null
          console.warn('⚠️ アップロード監視タイムアウト')
          await handleUploadComplete()
        }
      } catch (error) {
        console.error('❌ アップロード監視エラー:', error)
        if (uploadMonitorInterval) {
          clearInterval(uploadMonitorInterval)
          uploadMonitorInterval = null
        }
      }
    }, checkInterval)
  }

  const startProductionUploadProgress = async () => {
    try {
      if (reliableRecorder.value && reliableRecorder.value.recordingId) {
        currentRecordingId.value = reliableRecorder.value.recordingId
        const { IndexedDBStorage } = await import('../utils/indexedDBStorage.js')
        const storage = new IndexedDBStorage()
        const unuploadedChunks = await storage.getUnuploadedChunks()
        const currentRecordingChunks = unuploadedChunks.filter(
          chunk => chunk.recordingId === currentRecordingId.value
        )

        showProductionUploadProgress.value = true
        productionUploadProgress.value = {
          total: currentRecordingChunks.length,
          completed: 0,
          percentage: 0
        }
        uploadStatus.value = 'uploading'
        startUploadMonitoring()
      }
    } catch (error) {
      console.error('❌ アップロード進捗表示開始エラー:', error)
    }
  }

  const startRecording = async (day, type) => {
    if (!guardHoliday()) return

    const userId = currentUserRef?.value?.id
    if (!userId) {
      console.error('❌ 認証されていないユーザーです')
      alert('ログインが必要です。ページを更新してください。')
      return
    }

    try {
      console.log('🎤 録音開始準備:', { day, type, userId })

      // 録音開始ごとにリセット（自動停止でも onRecordingStopRequested を二重に呼ばないため）
      stopRequestedByUser = false

      reliableRecorder.value = new ReliableRecorder({
        onStart: () => {
          console.log('🎤 高信頼性録音開始:', { day, type })
          isRecording.value = true
          currentRecordingDay.value = day
          currentRecordingType.value = type
          recordingTime.value = 0
          canStopRecording.value = false
          productionUploadProgress.value = { total: 0, completed: 0, percentage: 0 }
          uploadStatus.value = 'uploading'
          startDisplayTimeCounter()
          onRecordingStarted()
        },
        onProgress: (progress) => {
          recordingTime.value = Math.floor(progress.recordingTime / 1000)
          if (progress.totalChunks >= 1 && !canStopRecording.value) {
            canStopRecording.value = true
            console.log('✅ チャンク作成完了、録音停止ボタンを有効化')
          }
        },
        onComplete: (result) => {
          console.log('✅ 高信頼性録音完了:', result)

          // 自動停止（最大時間到達など）の場合、stopRecording() 経由ではないため
          // `onRecordingStopRequested` が呼ばれず、親側の「送信済み」表示が出ないことがある。
          // そこで、手動停止でなければ onRecordingStopRequested を1回だけ発火させる。
          if (!stopRequestedByUser) {
            const resolvedType = result?.type || currentRecordingType.value
            const resolvedDurationSeconds = Math.max(
              0,
              Math.round(result?.duration ?? recordingTime.value ?? 0)
            )
            if (resolvedType) {
              try {
                onRecordingStopRequested(resolvedType, resolvedDurationSeconds)
              } catch (e) {
                console.warn('onRecordingStopRequested (auto):', e)
              }
            }
          }

          isRecording.value = false
          stopDisplayTimeCounter()
          const submissionResult = {
            ...result,
            submissionId: result.submissionId || Date.now(),
            audioUrl: result.audioUrl || ''
          }
          onSubmissionSuccess(submissionResult)
          currentRecordingDay.value = null
          currentRecordingType.value = null
          canStopRecording.value = false
        },
        onError: (err) => {
          console.error('❌ 高信頼性録音エラー:', err)
          isRecording.value = false
          currentRecordingDay.value = null
          currentRecordingType.value = null
          canStopRecording.value = false
          stopDisplayTimeCounter()

          // ReliableRecorder の onError は { type, error, ... } を渡す
          const errorMessage = err.error ?? err.message ?? String(err)
          if (errorMessage.includes('QuotaExceeded')) {
            alert('端末のストレージ容量が不足しているため、録音データを保存できませんでした。\n写真や動画、使っていないアプリなどを削除して、少なくとも 100MB 以上の空き容量を確保してから、もう一度録音をお試しください。')
          } else if (errorMessage.includes('Permission denied')) {
            alert('マイクへのアクセスが許可されていません')
          } else if (errorMessage.includes('NotSupportedError')) {
            alert('お使いのブラウザでは録音機能がサポートされていません')
          } else if (errorMessage.includes('DataCloneError')) {
            alert('録音データの保存中にエラーが発生しました。ページを再読み込みしてください。')
          } else {
            alert('録音中にエラーが発生しました: ' + errorMessage)
          }
        }
      })

      const learnerId = selectedLearnerIdRef?.value ?? null
      await reliableRecorder.value.startRecording({
        day,
        type,
        userId,
        learnerId
      })

      console.log('✅ 高信頼性録音開始完了')
    } catch (error) {
      console.error('録音開始エラー:', error)
      alert('マイクへのアクセスが許可されていません')
    }
  }

  const stopRecording = () => {
    console.log('⏹️ 録音停止ボタン押下')
    const type = currentRecordingType.value
    const durationSeconds = displayTime.value || recordingTime.value || 0

    stopRequestedByUser = true

    if (type) {
      try {
        onRecordingStopRequested(type, durationSeconds)
      } catch (e) {
        console.warn('onRecordingStopRequested:', e)
      }
    }
    isRecording.value = false
    stopDisplayTimeCounter()
    startProductionUploadProgress()

    nextTick(() => {
      setTimeout(async () => {
        try {
          if (reliableRecorder.value) {
            await reliableRecorder.value.stopRecording()
          }
        } catch (error) {
          console.error('❌ 録音停止エラー:', error)
        }
      }, 0)
    })
  }

  const startPracticeRecording = (day) => startRecording(day, 'practice')
  const startAssignmentRecording = (day) => startRecording(day, 'assignment')

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  return {
    isRecording,
    currentRecordingDay,
    currentRecordingType,
    canStopRecording,
    recordingTime,
    displayTime,
    showProductionUploadProgress,
    productionUploadProgress,
    uploadStatus,
    reliableRecorder,

    startRecording,
    stopRecording,
    startPracticeRecording,
    startAssignmentRecording,
    formatTime,
    startDisplayTimeCounter,
    stopDisplayTimeCounter
  }
}
